import React from 'react';
import { atomField, required } from 'atoms/form-atoms';
import { LandingAnalysisSettingsValues } from 'components/well-3d-viewer/utils';
import Highcharts from 'highcharts';
import { HOTKEY_SCOPES } from 'lib/constants';
import { format } from 'lib/ui';
import { isNil, snakeCase } from 'lodash-es';
import moment from 'moment';
import {
  DeclineCurveAnalysisParams,
  DiFormat,
  ForecastDurationOption,
  QiFormat,
  RampUpParams,
} from 'types/api-payloads';
import {
  DcaScenariosResponse,
  RatioRecord,
  VolumeRecord,
  WellLandingAnalysisPreview,
} from 'types/api-responses';
import {
  DcaScenarioResponse,
  Model,
  ModelType,
  ProductionStreamConfig,
  QiBoundType,
} from 'types/dca-scenarios-api/responses';
import { MineralType } from 'types/minerals';
import {
  parseLocalDateTime,
  parseMomentUtc,
  parseUTCdateTime,
} from 'utils/dates';
import {
  format0,
  format2,
  formatNumber,
  formatPercentScale,
} from 'utils/number';
import { LockedParameters } from './atoms';
import {
  ESTIMATED_ALPHA,
  PERMIT_MARKER_COLOR,
  withAlpha,
} from './gun-barrel-marker-style';
import {
  DeclineModel,
  EnrichedRatioRecord,
  EnrichedVolumeRecord,
  ForecastSettings,
  LandingAnalysisWellRow,
  PerforationAdjustmentValue,
  ProductionVolumeData,
  RampUpModel,
  StreamConfigs,
  VolumeModelForm,
  XAxisType,
} from './types';

export type ChartZoomState = {
  yAxis0Min: number;
  yAxis0Max: number;
  yAxis1Min: number;
  yAxis1Max: number;
};

/** Which production series each volume chart type plots. */
export const volumeMap: Record<
  string,
  'volume' | 'cumulative' | 'ratio' | 'materialBalanceTime'
> = {
  monthly: 'volume',
  logarithmic: 'volume',
  cumulative: 'cumulative',
  ratio: 'ratio',
  materialBalanceTime: 'materialBalanceTime',
};

// Generate the version label based on the version name, version ID, begin time, and end time
export function generateVersionLabel(
  versionName: string | undefined,
  versionId: string,
  beginTime: string,
  endTime: string | null,
  showTimeStamp: boolean
): string {
  const formatDate = showTimeStamp ? parseLocalDateTime : parseUTCdateTime;
  const formattedBeginTime = formatDate(beginTime, showTimeStamp);
  const formattedEndTime = endTime ? formatDate(endTime, showTimeStamp) : null;

  return `${versionName} ${
    versionId === 'current' ? 'Current' : ''
  } (${formattedBeginTime}${formattedEndTime ? ` - ${formattedEndTime}` : ''})`;
}

export function formatVolume(
  value: number | undefined | null,
  unit?: string,
  fallback = '-'
): string {
  if (value !== null && value !== undefined) {
    return `${format.number(value)} ${unit || ''}`;
  }
  return fallback;
}

export function formatVolumeWhole(
  value: number | undefined | null,
  unit?: string,
  fallback = '-'
): string {
  if (value !== null && value !== undefined) {
    // Remove decimals completely (truncate) and format with comma separators
    const wholeNumber = Math.trunc(value);
    return `${format.number(wholeNumber)} ${unit || ''}`;
  }
  return fallback;
}

// Number range validation helper for required fields
const numberInRange = (
  value: number | null,
  min: number,
  max: number,
  fieldName: string,
  decimals?: number
) => {
  if (value === null || value === undefined) {
    throw new Error(`${fieldName} is required`);
  }
  if (typeof value === 'string' && value === '') {
    throw new Error(`${fieldName} is required`);
  }
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    throw new Error(`${fieldName} must be a valid number`);
  }
  if (numValue < min || numValue > max) {
    // Use provided decimals or fallback to 2 decimal places
    const decimalPlaces = decimals ?? 2;
    throw new Error(
      `${fieldName} must be between ${formatNumber(
        min,
        0,
        decimalPlaces,
        'decimal',
        true
      )} and ${formatNumber(max, 0, decimalPlaces, 'decimal', true)}`
    );
  }
  return numValue;
};

// Integer range validation helper for required fields
const integerInRange = (
  value: number | null,
  min: number,
  max: number,
  fieldName: string
) => {
  if (value === null || value === undefined) {
    throw new Error(`${fieldName} is required`);
  }
  if (typeof value === 'string' && value === '') {
    throw new Error(`${fieldName} is required`);
  }
  const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
  if (isNaN(numValue) || !Number.isInteger(numValue)) {
    throw new Error(`${fieldName} must be a whole number`);
  }
  if (numValue < min || numValue > max) {
    throw new Error(
      `${fieldName} must be between ${formatNumber(
        min,
        0,
        0,
        'decimal',
        true
      )} and ${formatNumber(max, 0, 0, 'decimal', true)}`
    );
  }
  return numValue;
};

// Validation for required fields - they will only show errors when dirty
const requiredNumberInRange = (
  value: number | null,
  min: number,
  max: number,
  fieldName: string,
  decimals?: number
) => {
  return numberInRange(value, min, max, fieldName, decimals);
};

// Validation for required integer fields
const requiredIntegerInRange = (
  value: number | null,
  min: number,
  max: number,
  fieldName: string
) => {
  return integerInRange(value, min, max, fieldName);
};

export const createDeclineValidation = (
  diFormat: DiFormat,
  fieldName: 'Di' | 'Dlim'
) => {
  const validationFn = (value: number | null) => {
    const min = diFormat === 'nominal' ? 0.0001 : 0.01;
    const max = diFormat === 'nominal' ? 0.9999 : 99.99;
    const decimals = diFormat === 'nominal' ? 4 : 2;
    return requiredNumberInRange(value, min, max, fieldName, decimals);
  };

  return {
    validate: validationFn,
  };
};

export const createForecastSettingsForm = () => {
  return {
    forecastDuration: atomField<number | null>(30, {
      validate: (v) => requiredIntegerInRange(v, 1, 100, 'Forecast duration'),
    }),
    qiFormat: atomField<QiFormat>('months', {
      validate: (v) => required(v, 'Qi format is required'),
    }),
    diFormat: atomField<DiFormat>('effective', {
      validate: (v) => required(v, 'Di format is required'),
    }),
    forecastDurationOption: atomField<ForecastDurationOption>(
      'forecast_start',
      {
        validate: (v) => required(v, 'Forecast duration option is required'),
      }
    ),
  };
};

export const createVolumeModelForm = (
  declineModel: DeclineModel = 'modified_hyperbolic',
  rampupModel: RampUpModel = 'none',
  {
    qi,
    di,
    bFactor,
    forecastStartDate,
    dlim,
    diFormat = 'effective',
    qiRampup,
    rampupDuration,
    n,
    ri,
    tElf,
    m,
  }: Partial<
    Omit<DeclineCurveAnalysisParams, 'forecastStartDate'> & {
      forecastStartDate: Date | null;
      diFormat: DiFormat;
    } & RampUpParams & {
        ri?: number | null;
        tElf?: number | null;
        m?: number | null;
      }
  >
) => {
  return {
    rampupModelAtomField: atomField<RampUpModel>(rampupModel, {
      validate: (v) => required(v, 'Ramp-up model is required'),
    }),
    declineModelAtomField: atomField<DeclineModel>(declineModel, {
      validate: (v) => required(v, 'Decline model is required'),
    }),
    startDateAtomField: atomField<Date | null>(forecastStartDate ?? null, {
      validate: (v) => required(v, 'Start date is required'),
      dirty: forecastStartDate !== null && forecastStartDate !== undefined,
    }),
    qiAtomField: atomField<number | null>(qi ?? null, {
      validate: (v) => requiredIntegerInRange(v, 1, 100_000_000, 'Qi'),
      dirty: qi !== null && qi !== undefined,
    }),
    diAtomField: atomField<number | null>(di ?? null, {
      ...createDeclineValidation(diFormat, 'Di'),
      dirty: di !== null && di !== undefined,
    }),
    ...(rampupModel === 'constant'
      ? {
          qiRampupAtomField: atomField<number | null>(qiRampup ?? null, {
            validate: (v) =>
              requiredIntegerInRange(v, 0, 100_000_000, 'Ramp-up Qi'),
          }),
          rampupDurationAtomField: atomField<number | null>(
            rampupDuration ?? null,
            {
              validate: (v) =>
                requiredIntegerInRange(v, 0, 100_000, 'Ramp-up Duration'),
            }
          ),
        }
      : {}),
    ...(rampupModel === 'linear'
      ? {
          qiRampupAtomField: atomField<number | null>(qiRampup ?? null, {
            validate: (v) =>
              requiredIntegerInRange(
                v,
                -100_000_000,
                100_000_000,
                'Ramp-up Qi'
              ),
          }),
          rampupDurationAtomField: atomField<number | null>(
            rampupDuration ?? null,
            {
              validate: (v) =>
                requiredIntegerInRange(v, 0, 100_000, 'Ramp-up Duration'),
            }
          ),
          nAtomField: atomField<number | null>(n ?? null, {
            validate: (v) =>
              requiredNumberInRange(v, -1_000_000.0, 1_000_000.0, 'n (slope)'),
          }),
        }
      : {}),
    ...(rampupModel === 'exponential'
      ? {
          qiRampupAtomField: atomField<number | null>(qiRampup ?? null, {
            validate: (v) =>
              requiredIntegerInRange(v, 0, 100_000_000, 'Ramp-up Qi'),
          }),
          rampupDurationAtomField: atomField<number | null>(
            rampupDuration ?? null,
            {
              validate: (v) =>
                requiredIntegerInRange(v, 0, 100_000, 'Ramp-up Duration'),
            }
          ),
          nAtomField: atomField<number | null>(n ?? null, {
            validate: (v) =>
              requiredNumberInRange(
                v,
                0.0001,
                9.9999,
                'n (exponential slope)',
                4
              ),
          }),
        }
      : {}),
    ...(declineModel === 'hyperbolic' || declineModel === 'modified_hyperbolic'
      ? {
          bFactorAtomField: atomField<number | null>(bFactor ?? null, {
            validate: (v) => requiredNumberInRange(v, 0.01, 9.99, 'b-factor'),
            dirty: bFactor !== null && bFactor !== undefined,
          }),
        }
      : {}),
    ...(declineModel === 'modified_hyperbolic'
      ? {
          dlimAtomField: atomField<number | null>(dlim ?? null, {
            ...createDeclineValidation(diFormat, 'Dlim'),
            dirty: dlim !== null && dlim !== undefined,
          }),
        }
      : {}),
    ...(declineModel === 'ratio'
      ? {
          riAtomField: atomField<number | null>(ri ?? null, {
            validate: (v) => requiredNumberInRange(v, 0.01, 10000, 'Ri'),
            dirty: ri !== null && ri !== undefined,
          }),
          tElfAtomField: atomField<number | null>(tElf ?? null, {
            validate: (v) => requiredIntegerInRange(v, 1, 10000, 't_elf'),
            dirty: tElf !== null && tElf !== undefined,
          }),
          mAtomField: atomField<number | null>(m ?? null, {
            validate: (v) => requiredNumberInRange(v, -1.0, 1.0, 'm'),
            dirty: m !== null && m !== undefined,
          }),
        }
      : {}),
  };
};

/**
 * Safely parses a numeric string and sets the value using the given setter
 * function. Empty values (empty string) are converted to null, while invalid values are
 * considered intermediate and will not be set.
 * @param {string} value The value to parse.
 * @param {function} setter The setter function to update the state.
 */
export function parseNumberAndSet(
  value: string,
  setter: (val: number | null) => void
) {
  if (value === '') {
    setter(null);
    return;
  }
  const parsedValue = parseFloat(value);
  if (isFinite(parsedValue)) {
    setter(parsedValue);
  }
}

// Helper to prevent non-numeric input for integers (`qi`)
export function handleIntegerKeyPress(
  event: React.KeyboardEvent<HTMLInputElement>,
  allowNegative = false
) {
  const { key, ctrlKey, metaKey } = event;

  if (allowNegative && key === '-') {
    const input = event.currentTarget;
    if (input.selectionStart === null || input.selectionStart === 0) {
      return;
    }
  }
  // Allow: digits 0-9
  if (/^[0-9]$/.test(key)) {
    return;
  }

  // Allow: essential navigation and editing keys
  if (
    [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
    ].includes(key)
  ) {
    return;
  }

  // Allow: copy/paste/select shortcuts (Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z)
  if (
    (ctrlKey || metaKey) &&
    ['a', 'c', 'v', 'x', 'z'].includes(key.toLowerCase())
  ) {
    return;
  }

  // Block everything else
  event.preventDefault();
}

export const hasDeclineModelChanged = (
  modelForm: VolumeModelForm,
  modelFormPrev?: VolumeModelForm | null
) => {
  return (
    modelForm.startDate !== modelFormPrev?.startDate ||
    modelForm.qi !== modelFormPrev?.qi ||
    modelForm.di !== modelFormPrev?.di ||
    modelForm.declineModel !== modelFormPrev?.declineModel ||
    modelForm.bFactor !== modelFormPrev?.bFactor ||
    modelForm.dlim !== modelFormPrev?.dlim ||
    modelForm.diFormat !== modelFormPrev?.diFormat ||
    modelForm.qiFormat !== modelFormPrev?.qiFormat ||
    modelForm.forecastDuration !== modelFormPrev?.forecastDuration ||
    modelForm.rampupModel !== modelFormPrev?.rampupModel ||
    modelForm.qiRampup !== modelFormPrev?.qiRampup ||
    modelForm.rampupDuration !== modelFormPrev?.rampupDuration ||
    modelForm.n !== modelFormPrev?.n ||
    modelForm.ri !== modelFormPrev?.ri ||
    modelForm.tElf !== modelFormPrev?.tElf ||
    modelForm.m !== modelFormPrev?.m ||
    modelForm.forecastDurationOption !== modelFormPrev?.forecastDurationOption
  );
};

const createRateHotkeyConfig = (
  startValue: number,
  isInteger: boolean = true
) => ({
  incrementKey: ['8'],
  decrementKey: ['2'],
  increment: (prev: number | null, multiplier: number) => {
    const result = prev ? prev + multiplier * Math.abs(prev / 10) : startValue;
    return isInteger
      ? Math.floor(result)
      : Number.isInteger(result)
        ? result
        : Math.round(result * 100) / 100;
  },
  decrement: (prev: number | null, multiplier: number) => {
    const result = prev ? prev - multiplier * Math.abs(prev / 10) : startValue;
    return isInteger
      ? Math.floor(result)
      : Number.isInteger(result)
        ? result
        : Math.round(result * 100) / 100;
  },
});

const creatExponentHotkeyConfig = (startValue: number) => ({
  incrementKey: ['NumpadAdd', 'shift+equal'],
  decrementKey: ['NumpadSubtract', 'shift+minus'],
  increment: (prev: number | null, multiplier: number) => {
    const result = !isNil(prev) ? prev + multiplier * 0.05 : startValue;
    return Number.isInteger(result) ? result : Math.round(result * 100) / 100;
  },
  decrement: (prev: number | null, multiplier: number) => {
    const result = !isNil(prev) ? prev - multiplier * 0.05 : startValue;
    return Number.isInteger(result) ? result : Math.round(result * 100) / 100;
  },
});

const durationHotkeyConfig = {
  incrementKey: ['right'],
  decrementKey: ['left'],
  increment: (prev: number | null, multiplier: number) => {
    return !isNil(prev) ? prev + multiplier * 1 : 0;
  },
  decrement: (prev: number | null, multiplier: number) => {
    return !isNil(prev) ? prev - multiplier * 1 : 0;
  },
};

export const DCA_FORM_HOTKEY_CONFIG = {
  qi: createRateHotkeyConfig(10000, true),
  ri: createRateHotkeyConfig(1, false),
  di: {
    incrementKey: ['6'],
    decrementKey: ['4'],
    increment: (prev: number | null, multiplier: number) => {
      const result = prev ? prev + multiplier * 1 : 50;
      return Number.isInteger(result) ? result : Math.round(result * 100) / 100;
    },
    decrement: (prev: number | null, multiplier: number) => {
      const result = prev ? prev - multiplier * 1 : 50;
      return Number.isInteger(result) ? result : Math.round(result * 100) / 100;
    },
  },
  bFactor: creatExponentHotkeyConfig(1),
  m: creatExponentHotkeyConfig(0),
  dlim: {
    incrementKey: ['9'],
    decrementKey: ['3'],
    increment: (prev: number | null, multiplier: number) => {
      const result = prev ? prev + multiplier * 0.05 : 7;
      return Number.isInteger(result) ? result : Math.round(result * 100) / 100;
    },
    decrement: (prev: number | null, multiplier: number) => {
      const result = prev ? prev - multiplier * 0.05 : 7;
      return Number.isInteger(result) ? result : Math.round(result * 100) / 100;
    },
  },
  rampupQi: {
    incrementKey: ['up'],
    decrementKey: ['down'],
    increment: (prev: number | null, multiplier: number) => {
      return prev ? Math.floor(prev + multiplier * Math.abs(prev / 10)) : 10000;
    },
    decrement: (prev: number | null, multiplier: number) => {
      return prev ? Math.floor(prev - multiplier * Math.abs(prev / 10)) : 10000;
    },
  },
  rampupDuration: durationHotkeyConfig,
  tElf: durationHotkeyConfig,
  rampupN: {
    incrementKey: ['7'],
    decrementKey: ['1'],
    increment: (prev: number | null, multiplier: number) => {
      const result = prev ? prev + multiplier * Math.abs(prev / 10) : 1.5;
      return Number.isInteger(result) ? result : Math.round(result * 100) / 100;
    },
    decrement: (prev: number | null, multiplier: number) => {
      const result = prev ? prev - multiplier * Math.abs(prev / 10) : 1.5;
      return Number.isInteger(result) ? result : Math.round(result * 100) / 100;
    },
  },
};

/** Key list for a parameter no key changes in a given mapping. */
const NO_KEYS: string[] = [];

/**
 * Copies a parameter's config with different increment and decrement keys.
 *
 * @param {object} config - The parameter config to copy.
 * @param {string[]} incrementKey - Keys that increment the parameter.
 * @param {string[]} decrementKey - Keys that decrement it.
 * @return {object} The copy.
 */
const withKeys = <T extends { incrementKey: string[]; decrementKey: string[] }>(
  config: T,
  incrementKey: string[],
  decrementKey: string[]
): T => ({ ...config, incrementKey, decrementKey });

/**
 * The ARIES key mapping. It steps each parameter by the same amount Insights
 * does, but binds only the three parameters ARIES itself exposes: qi (Ri on a
 * ratio model) on up/down, Di on left/right, and b-factor on plus/minus. The
 * rest get an empty key list, so no key changes them in this mapping.
 */
export const ARIES_FORM_HOTKEY_CONFIG: typeof DCA_FORM_HOTKEY_CONFIG = {
  ...DCA_FORM_HOTKEY_CONFIG,
  qi: withKeys(DCA_FORM_HOTKEY_CONFIG.qi, ['up'], ['down']),
  ri: withKeys(DCA_FORM_HOTKEY_CONFIG.ri, ['up'], ['down']),
  di: withKeys(DCA_FORM_HOTKEY_CONFIG.di, ['right'], ['left']),
  dlim: withKeys(DCA_FORM_HOTKEY_CONFIG.dlim, NO_KEYS, NO_KEYS),
  m: withKeys(DCA_FORM_HOTKEY_CONFIG.m, NO_KEYS, NO_KEYS),
  rampupQi: withKeys(DCA_FORM_HOTKEY_CONFIG.rampupQi, NO_KEYS, NO_KEYS),
  rampupDuration: withKeys(
    DCA_FORM_HOTKEY_CONFIG.rampupDuration,
    NO_KEYS,
    NO_KEYS
  ),
  rampupN: withKeys(DCA_FORM_HOTKEY_CONFIG.rampupN, NO_KEYS, NO_KEYS),
  tElf: withKeys(DCA_FORM_HOTKEY_CONFIG.tElf, NO_KEYS, NO_KEYS),
};

export const HOTKEY_CONFIG: Record<
  HOTKEY_SCOPES,
  typeof DCA_FORM_HOTKEY_CONFIG
> = {
  DCA_INPUT_FORM_OIL: DCA_FORM_HOTKEY_CONFIG,
  DCA_INPUT_FORM_GAS: DCA_FORM_HOTKEY_CONFIG,
  DCA_INPUT_FORM_WATER: DCA_FORM_HOTKEY_CONFIG,
} as const;

/** `ARIES_FORM_HOTKEY_CONFIG` for each stream scope. */
export const ARIES_HOTKEY_CONFIG: Record<
  HOTKEY_SCOPES,
  typeof DCA_FORM_HOTKEY_CONFIG
> = {
  DCA_INPUT_FORM_OIL: ARIES_FORM_HOTKEY_CONFIG,
  DCA_INPUT_FORM_GAS: ARIES_FORM_HOTKEY_CONFIG,
  DCA_INPUT_FORM_WATER: ARIES_FORM_HOTKEY_CONFIG,
} as const;

/** The parameter key mappings hotkeys mode can switch between. */
export const HOTKEY_CONFIG_BY_MAPPING = {
  insights: HOTKEY_CONFIG,
  aries: ARIES_HOTKEY_CONFIG,
} as const;

/** Name of a parameter key mapping. */
export type DcaHotkeyMapping = keyof typeof HOTKEY_CONFIG_BY_MAPPING;

// Calculates the maximum months online for a given stream
const calculateMaxMonths = (
  stream: 'oil' | 'gas' | 'water',
  volumes: Array<VolumeRecord>,
  minStartDate: Date
): number => {
  const lastVolumeForStream = volumes
    .slice()
    .reverse()
    .find((volume) => volume[stream]);

  if (!lastVolumeForStream) {
    return 0;
  }

  const lastProductionDateForStream = new Date(lastVolumeForStream.date);
  const monthsOnline = Math.abs(
    parseMomentUtc(lastProductionDateForStream).diff(
      parseMomentUtc(minStartDate),
      'months'
    )
  );
  return monthsOnline;
};

// Finds the correct stream config for a stream.
// Only considers configs with modelType "biased" and picks the one with the smallest
// maxMonthsOnline that is still greater than the computed maxMonths for this stream.
const findStreamConfig = (
  stream: 'oil' | 'gas' | 'water',
  maxMonths: number,
  productionStreamConfigs: ProductionStreamConfig[],
  modelType: 'biased' | 'ramp_up' = 'biased'
): ProductionStreamConfig | undefined => {
  // Filter for biased configs matching the stream
  const biasedConfigs = productionStreamConfigs.filter(
    (config: ProductionStreamConfig) =>
      config.stream === stream && config.modelType === modelType
  );
  // Find the one with the smallest maxMonthsOnline that is still greater than maxMonths
  const matchingConfigs = biasedConfigs.filter(
    (config) => config.maxMonthsOnline > maxMonths
  );

  if (matchingConfigs.length === 0) {
    return undefined;
  }

  // Return the config with the smallest maxMonthsOnline
  return matchingConfigs.reduce((prev, current) => {
    return current.maxMonthsOnline < prev.maxMonthsOnline ? current : prev;
  });
};

// Calculates stream configs from scenario and production data
export const calculateStreamConfigs = (
  productionStreamConfigs: ProductionStreamConfig[],
  productionVolume: ProductionVolumeData
): StreamConfigs | null => {
  // Get first production dates for each stream
  const firstOilEntry = productionVolume.volumes.find(
    (volume) => volume.oil && volume.oil > 0
  );
  const firstGasEntry = productionVolume.volumes.find(
    (volume) => volume.gas && volume.gas > 0
  );
  const firstWaterEntry = productionVolume.volumes.find(
    (volume) => volume.water && volume.water > 0
  );

  const minStartDates = [
    firstOilEntry?.date,
    firstGasEntry?.date,
    firstWaterEntry?.date,
  ].filter(Boolean) as string[];

  if (minStartDates.length === 0) return null;

  const minStartDate = new Date(
    Math.min(...minStartDates.map((d) => new Date(d).getTime()))
  );

  const oilMaxMonths = calculateMaxMonths(
    'oil',
    productionVolume.volumes,
    minStartDate
  );
  const gasMaxMonths = calculateMaxMonths(
    'gas',
    productionVolume.volumes,
    minStartDate
  );
  const waterMaxMonths = calculateMaxMonths(
    'water',
    productionVolume.volumes,
    minStartDate
  );

  const oilDcaModel = findStreamConfig(
    'oil',
    oilMaxMonths,
    productionStreamConfigs
  );

  const oilRampUpModel = findStreamConfig(
    'oil',
    oilMaxMonths,
    productionStreamConfigs,
    'ramp_up'
  );
  const gasDcaModel = findStreamConfig(
    'gas',
    gasMaxMonths,
    productionStreamConfigs
  );
  const gasRampUpModel = findStreamConfig(
    'gas',
    gasMaxMonths,
    productionStreamConfigs,
    'ramp_up'
  );
  const waterDcaModel = findStreamConfig(
    'water',
    waterMaxMonths,
    productionStreamConfigs
  );
  const waterRampUpModel = findStreamConfig(
    'water',
    waterMaxMonths,
    productionStreamConfigs,
    'ramp_up'
  );
  return {
    oilConfig: {
      dca: oilDcaModel,
      rampUp: oilRampUpModel,
    },
    gasConfig: {
      dca: gasDcaModel,
      rampUp: gasRampUpModel,
    },
    waterConfig: {
      dca: waterDcaModel,
      rampUp: waterRampUpModel,
    },
  };
};

// Extracts forecast settings from scenario data
export const extractForecastSettings = (
  scenarioData: DcaScenarioResponse
): ForecastSettings => {
  const durationPeriod =
    scenarioData.durationFormat === 'yearly'
      ? scenarioData.durationPeriod
      : Math.round(scenarioData.durationPeriod / 12); // Months to years

  const qiFormat: QiFormat =
    scenarioData.qiFormat === 'monthly' ? 'months' : 'days';
  const diFormat: DiFormat = scenarioData.diFormat as DiFormat;

  return {
    durationPeriod,
    durationOption: scenarioData.durationOption as ForecastDurationOption,
    qiFormat,
    diFormat,
  };
};

interface ConvertDateToXAxisValueParams {
  date: Date;
  targetXAxisType: XAxisType;
  production: {
    volume: VolumeRecord[];
    cumulative: VolumeRecord[];
    ratio: RatioRecord[];
    materialBalanceTime: VolumeRecord[];
  };
  forecast: {
    volume: VolumeRecord[];
    cumulative: VolumeRecord[];
    ratio: RatioRecord[];
    materialBalanceTime: VolumeRecord[];
  };
  chartType: 'monthly' | 'logarithmic' | 'cumulative' | 'ratio';
  earliestDate: moment.Moment;
}

// Converts a date to the corresponding x-axis value based on the target x-axis type
export const convertDateToXAxisValue = ({
  date,
  targetXAxisType,
  production,
  forecast,
  chartType,
  earliestDate,
}: ConvertDateToXAxisValueParams): number | null => {
  // Enrich the data with cumulative and material balance time values
  const enrichVolumeRecord = (
    baseArray: VolumeRecord[] | RatioRecord[],
    cumulativeArray: VolumeRecord[],
    mbtArray: VolumeRecord[]
  ) => {
    return baseArray.map((baseRecord) => {
      const matchingCumulative = cumulativeArray.find(
        (cumRecord) => cumRecord.date === baseRecord.date
      );
      const matchingMBT = mbtArray.find(
        (mbtRecord) => mbtRecord.date === baseRecord.date
      );

      return {
        ...baseRecord,
        cumulativeOil: matchingCumulative?.oil,
        cumulativeWater: matchingCumulative?.water,
        cumulativeGas: matchingCumulative?.gas,
        materialBalanceTimeOil: matchingMBT?.oil,
        materialBalanceTimeWater: matchingMBT?.water,
        materialBalanceTimeGas: matchingMBT?.gas,
      };
    });
  };

  const dataType = volumeMap[chartType];

  // Combine production and forecast data
  const allRecords = [
    ...enrichVolumeRecord(
      production[dataType],
      production.cumulative,
      production.materialBalanceTime
    ),
    ...enrichVolumeRecord(
      forecast[dataType],
      forecast.cumulative,
      forecast.materialBalanceTime
    ),
  ] as EnrichedVolumeRecord[] | EnrichedRatioRecord[];

  // Find the record matching the given date (compare at day level, ignoring time)
  const targetDateStr = moment.utc(date).format('YYYY-MM-DD');
  const matchingRecord = allRecords.find(
    (record) => moment.utc(record.date).format('YYYY-MM-DD') === targetDateStr
  );

  if (!matchingRecord) {
    // No matching record found, return null
    return null;
  }

  // Calculate x-axis value based on target type
  switch (targetXAxisType) {
    case 'date':
      return moment.utc(matchingRecord.date).valueOf();

    case 'count':
      return moment.utc(matchingRecord.date).diff(earliestDate, 'months') + 1;

    case 'cumulativeOil':
      return matchingRecord.cumulativeOil ?? null;

    case 'cumulativeWater':
      return matchingRecord.cumulativeWater ?? null;

    case 'cumulativeGas':
      return matchingRecord.cumulativeGas ?? null;

    case 'materialBalanceTimeOil':
      return matchingRecord.materialBalanceTimeOil ?? null;

    case 'materialBalanceTimeWater':
      return matchingRecord.materialBalanceTimeWater ?? null;

    case 'materialBalanceTimeGas':
      return matchingRecord.materialBalanceTimeGas ?? null;

    default:
      return null;
  }
};

export const isLocked = (
  lockParams: LockedParameters,
  stream: 'oil' | 'gas' | 'water',
  type: 'dca' | 'rampUp',
  parameter: string
) => {
  return !isNil(lockParams[stream as keyof typeof lockParams][type][parameter]);
};

export const getInitialLockedParameters = (
  configs: StreamConfigs
): LockedParameters => {
  const initialLockedParameters = Object.entries(configs).reduce(
    (acc, [key, value]) => {
      const stream =
        key === 'oilConfig' ? 'oil' : key === 'gasConfig' ? 'gas' : 'water';

      acc[stream] = {
        dca: value?.dca?.lockedParameters || {},
        rampUp: value?.rampUp?.lockedParameters || {},
      };
      return acc;
    },
    {} as LockedParameters
  );

  return initialLockedParameters;
};

/**
 * Returns the valid range for one decline-curve parameter: the bounds a DCA
 * hotkey step is clamped to.
 *
 * Near-copy of `getParameterRange` in
 * `pages/dca-scenarios/dca-scenario-form/utils.ts`. Three lower bounds differ:
 * `bFactor` and `ri` (0 here, 0.01 there) and ramp-up exponential `n`
 * (0 here, 0.0001 there), so a hotkey can reach a value the scenario form
 * rejects.
 *
 * @param {string} paramName - The parameter name, such as `qi`, `di` or `bFactor`.
 * @param {ModelType} modelType - The model type; `ramp_up` changes some ranges.
 * @param {Model} model - The decline or ramp-up model.
 * @param {QiBoundType} qiBoundType - Whether `qi` is bounded relatively or absolutely.
 * @param {DiFormat} diFormat - Whether decline rates are effective or nominal.
 * @return {[number, number]} The inclusive minimum and maximum.
 */
export const getValidationRange = (
  paramName: string,
  modelType: ModelType,
  model: Model,
  qiBoundType: QiBoundType,
  diFormat: DiFormat
): [number, number] => {
  switch (paramName) {
    case 'qi':
      if (modelType === 'ramp_up' && model === 'linear') {
        if (qiBoundType === 'relative') return [-999.99, 999.99];
        else return [-100000000, 100000000];
      }

      if (qiBoundType === 'relative') return [0.01, 999.99];

      if (modelType === 'ramp_up') return [0, 100000000];

      return [1, 100000000];

    case 'di':
    case 'dlim':
      if (diFormat === 'effective') {
        return [0.01, 99.99];
      }
      return [0.0001, 0.9999];

    case 'n':
      if (modelType === 'ramp_up' && model === 'exponential') {
        return [0, 9.9999];
      }
      if (modelType === 'ramp_up' && model === 'linear') {
        return [-1000000, 1000000];
      }
      return [0, 100000];

    case 'bFactor':
      return [0, 9.99];

    case 'ri':
      return [0, 10000];

    case 'tElf':
      return [1, 10000];

    case 'm':
      return [-1, 1];

    default:
      return [0, 100000000];
  }
};

/**
 * Whether a date is one of a stream's outlier dates. With `byMonth`, dates
 * match on their month alone: the regression marks a month as an outlier, so
 * every daily row in that month is one.
 *
 * @param {string[] | undefined} outlierDates - The stream's outlier dates, `YYYY-MM-DD`.
 * @param {string} date - The row's date, `YYYY-MM-DD`.
 * @param {boolean} byMonth - Match on the month rather than the exact date.
 * @return {boolean} True when the date is an outlier.
 */
export const isOutlierDate = (
  outlierDates: string[] | undefined,
  date: string,
  byMonth = false
): boolean => {
  if (!outlierDates) return false;
  if (!byMonth) return outlierDates.includes(date);
  const month = monthOf(date);
  return outlierDates.some((outlierDate) => monthOf(outlierDate) === month);
};

/**
 * A date's month.
 *
 * @param {string} date - A `YYYY-MM-DD` date.
 * @return {string} The month as `YYYY-MM`.
 */
export const monthOf = (date: string) => date.slice(0, 7);

/**
 * The date of a well's first production row, in any stream.
 *
 * @param {{ date: string }[]} rows - The well's production rows.
 * @return {number | null} The date as a timestamp, or null without any rows.
 */
export const findFirstProductionDate = (
  rows: { date: string }[]
): number | null =>
  rows.reduce<number | null>((first, row) => {
    if (!row.date) return first;
    const date = moment.utc(row.date).valueOf();
    return first === null || date < first ? date : first;
  }, null);

/**
 * A point's months online. The month of the well's first production row is
 * month 1, as in the forecast table, which counts calendar months, so a daily
 * row partway through a month is in the same month as the 1st. Without
 * production, the month of the series' first point is month 1.
 *
 * @param {number} pointDate - The point's date, as a timestamp.
 * @param {number | null} firstProductionDate - From `findFirstProductionDate`.
 * @param {number} seriesFirstDate - The date of the series' first point.
 * @return {number} The months online.
 */
export const monthsOnlineAt = (
  pointDate: number,
  firstProductionDate: number | null,
  seriesFirstDate: number
): number =>
  moment
    .utc(pointDate)
    .startOf('month')
    .diff(
      moment.utc(firstProductionDate ?? seriesFirstDate).startOf('month'),
      'months'
    ) + 1;

/**
 * A source's current version, or its first listed one when none is current.
 *
 * @param {T[]} versions - The source's versions.
 * @return {T | undefined} The version to start on, or undefined without any.
 */
export const currentOrFirstVersion = <T extends { versionId: string }>(
  versions: T[]
): T | undefined =>
  versions.find((version) => version.versionId === 'current') ?? versions[0];

/**
 * The production record nearest an x value, where a dragged segment start
 * snaps to on the Cumulative and Material Balance Time axes. With
 * `toMonthStart`, the date is the first of the record's month, since daily
 * rows fall mid-month and the regression works in months.
 *
 * @param {T[]} records - The production records on the chart.
 * @param {number} x - The dragged x value.
 * @param {(record: T) => number} xOf - A record's x value.
 * @param {boolean} toMonthStart - Snap the date to the start of its month.
 * @return {{ date: Date; x: number } | null} The snapped date and the record's x, or null without records.
 */
export const snapToNearestRecord = <T extends { date: string }>(
  records: T[],
  x: number,
  xOf: (record: T) => number,
  toMonthStart: boolean
): { date: Date; x: number } | null => {
  let nearest: { record: T; x: number } | null = null;
  for (const record of records) {
    const recordX = xOf(record);
    if (!nearest || Math.abs(recordX - x) < Math.abs(nearest.x - x)) {
      nearest = { record, x: recordX };
    }
  }
  if (!nearest) return null;

  const date = moment.utc(nearest.record.date);
  return {
    date: (toMonthStart ? date.startOf('month') : date).toDate(),
    x: nearest.x,
  };
};

/**
 * Whether a production point is an outlier for its stream. A ratio is an
 * outlier when either of its two streams is.
 *
 * @param {string} source - The series the point is on; only Production has outliers.
 * @param {string} stream - The stream or ratio.
 * @param {string} type - The chart type.
 * @param {string} date - The point's date, `YYYY-MM-DD`.
 * @param {Record<MineralType, string[]>} outlierDates - Each stream's outlier dates.
 * @param {boolean} byMonth - Match outlier dates on their month, for daily rows.
 * @return {boolean} True when the point is an outlier.
 */
export const isPointOutlier = (
  source: string,
  stream: string,
  type: 'monthly' | 'logarithmic' | 'cumulative' | 'ratio',
  date: string,
  outlierDates?: Record<MineralType, string[]>,
  byMonth = false
): boolean => {
  if (source !== 'Production' || !outlierDates) {
    return false;
  }

  if (type === 'ratio') {
    const isOilOutlier = isOutlierDate(outlierDates.oil, date, byMonth);
    const isGasOutlier = isOutlierDate(outlierDates.gas, date, byMonth);
    const isWaterOutlier = isOutlierDate(outlierDates.water, date, byMonth);

    if (stream === 'gor') {
      return isOilOutlier || isGasOutlier;
    } else if (stream === 'wor') {
      return isOilOutlier || isWaterOutlier;
    } else if (stream === 'wgr') {
      return isWaterOutlier || isGasOutlier;
    } else if (stream === 'cgr') {
      return isOilOutlier || isGasOutlier;
    }
  } else {
    return isOutlierDate(outlierDates[stream as MineralType], date, byMonth);
  }

  return false;
};

export const getUnlockedParameters = (
  lockedParameters: LockedParameters,
  modelSpec: {
    oil?: {
      dca: DeclineModel;
      rampUp: RampUpModel;
    };
    gas?: {
      dca: DeclineModel;
      rampUp: RampUpModel;
    };
    water?: {
      dca: DeclineModel;
      rampUp: RampUpModel;
    };
  }
) => {
  // all possible model parameters
  const parameterMap = {
    dca: {
      modified_hyperbolic: ['qi', 'di', 'bFactor', 'dlim'],
      hyperbolic: ['qi', 'di', 'bFactor'],
      exponential: ['qi', 'di'],
      ratio: ['ri', 'tElf', 'm'],
      // linear: ['qi', 'n'],
      // constant: ['qi'],
    },
    rampUp: {
      linear: ['qi', 'n'],
      exponential: ['qi', 'n'],
      constant: ['qi'],
    },
  };

  const result: {
    [K in 'oil' | 'gas' | 'water']: {
      dca: string[];
      rampUp: string[];
    };
  } = {
    oil: { dca: [], rampUp: [] },
    gas: { dca: [], rampUp: [] },
    water: { dca: [], rampUp: [] },
  };

  for (const stream of Object.keys(modelSpec) as ('oil' | 'gas' | 'water')[]) {
    const streamLocked = lockedParameters[stream] || {};
    result[stream] = { dca: [], rampUp: [] };

    for (const modelType of ['dca', 'rampUp'] as const) {
      const modelName = modelSpec[stream];
      const expectedParams: string[] =
        parameterMap[modelType as keyof typeof parameterMap][
          modelName?.[
            modelType as keyof typeof modelName
          ] as keyof (typeof parameterMap)[keyof typeof parameterMap]
        ] ||
        (modelType === 'dca'
          ? parameterMap.dca.modified_hyperbolic
          : parameterMap.rampUp.constant);

      // If there are no parameters for this model (empty expectedParams), all are considered unlocked
      if (expectedParams.length === 0) {
        result[stream][modelType] = [];
        continue;
      }

      // Retrieve which params are actually locked in lockedParameters for this modelType
      const lockedParamsObj =
        streamLocked[modelType as keyof typeof streamLocked] || {};
      const lockedParamNames = Object.keys(lockedParamsObj);

      // Unlocked params are those in expectedParams but not in lockedParamNames
      const unlockedParams = expectedParams.filter(
        (param) => !lockedParamNames.includes(param)
      );
      result[stream][modelType] = unlockedParams.map((param) =>
        snakeCase(param)
      );
    }
  }

  return result;
};

// Converts a decimal factor (e.g., 0.95) to a PerforationAdjustmentValue string (e.g., '95')
export const convertFactorToValue = (
  factor: number | null
): PerforationAdjustmentValue => {
  if (factor === null) return 'none';
  const percentage = Math.round(factor * 100);
  if (percentage === 92) return '92';
  if (percentage === 95) return '95';
  if (percentage === 98) return '98';
  if (percentage === 99) return '99';
  return 'none';
};

// wells.stick_estimated_lateral_factor is stored as a fraction in (0, 1] with at most 3 decimals
// (the server rejects a finer one), and edited and shown as a percent in (0, 100] to one decimal,
// like the perforation factor beside it. These convert at that boundary.
const STICK_PERCENT_DECIMALS = 1;

// Mirrors the backend constant Query::WellProducingLengthLookup::StickLateral::STICK_TO_LATERAL_FACTOR,
// which is also the `wells.stick_estimated_lateral_factor` column default (NOT NULL DEFAULT 0.925,
// db/migrate/20260930200236_add_stick_estimated_lateral_factor_to_wells.rb). Change all three
// together. Every well arrives with its own value; this is only what the editor's copy calls
// "the default".
export const DEFAULT_STICK_LATERAL_FACTOR = 0.925;
const STICK_PERCENT_SCALE = 10 ** STICK_PERCENT_DECIMALS;

// Rounds a typed percent to the one decimal the server accepts: 92.55 -> 92.6.
export const roundStickLateralPercent = (percent: number) =>
  Math.round(percent * STICK_PERCENT_SCALE) / STICK_PERCENT_SCALE;

// 0.925 -> 92.5. Rounded, so float noise (0.925 * 100 = 92.50000000000001) never shows.
export const stickLateralFactorToPercent = (factor: number) =>
  roundStickLateralPercent(factor * 100);

// 92.5 -> 0.925, rounded to 3 decimals so the stored fraction always passes the server's check.
export const stickLateralPercentToFactor = (percent: number) =>
  Math.round(percent * STICK_PERCENT_SCALE) / (100 * STICK_PERCENT_SCALE);

// Judged after rounding: 0.04 rounds to 0 and is rejected; 100.04 rounds to 100 and is allowed.
export const isValidStickLateralPercent = (
  percent: number | null
): percent is number => {
  if (percent === null || !Number.isFinite(percent)) return false;
  const rounded = roundStickLateralPercent(percent);
  return rounded > 0 && rounded <= 100;
};

// 0.925 -> "92.5%", 0.9 -> "90%": the event text's format (StickEstimatedLateralFactor).
export const formatStickLateralFactor = (factor: number) =>
  formatPercentScale(
    stickLateralFactorToPercent(factor),
    undefined,
    STICK_PERCENT_DECIMALS
  );

// Converts a PerforationAdjustmentValue string (e.g., '95') to a decimal factor (e.g., 0.95)
export const convertValueToFactor = (
  value: PerforationAdjustmentValue
): number | null => {
  if (value === null || value === 'none') return null;
  return parseInt(value, 10) / 100;
};

//  Maps a single wellbore + its landing analysis result into a table row.
// Used by both SubjectWellTable and OffsetWellTable.
export function buildLandingAnalysisWellRow(
  uwi14: string,
  type: string,
  source: string | null,
  analysis: WellLandingAnalysisPreview | undefined
): LandingAnalysisWellRow {
  return {
    id: uwi14,
    uwi14,
    analysisPreviewId: analysis?.id ?? null,
    isPreview: analysis?.isPreview ?? false,
    // Type column. Pre-drill rows show the result's completionType ('Permit'),
    // else the capitalized bore type (also the fallback before the result loads).
    // Gated on geometrySourceType so drilled behavior is unchanged.
    wellboreType:
      analysis?.geometrySourceType === 'pre_drill' &&
      analysis?.wellLevel?.completionType
        ? analysis.wellLevel.completionType
        : type.charAt(0).toUpperCase() + type.slice(1),
    wellboreTrajectory: analysis?.wellLevel?.wellboreTrajectory ?? '--',
    structureGridCollection: {
      id: analysis?.structureGridCollection?.id ?? null,
      name: analysis?.structureGridCollection?.collectionName ?? '--',
      href: analysis?.structureGridCollection?.href ?? null,
      isDefault: analysis?.structureGridCollection?.isDefault ?? false,
    },
    source,
    analysisType: analysis?.analysisType ?? null,
    sourceRunAt: analysis?.sourceRunAt ?? null,
    markedAt: analysis?.markedAt ?? null,
    savedResult: null,
    // The interval alone where it maps to no type-curve formation (UAT item 7: an interval-only
    // pick on a no-grid well read as a blank zone).
    landingZone: (() => {
      const interval = analysis?.wellLevel?.landingZoneInterval;
      const formation = analysis?.wellLevel?.typeCurveFormation;
      if (!interval) return '--';
      return formation ? `${interval} (${formation})` : interval;
    })(),
    landingZoneGridName: analysis?.wellLevel?.landingZoneGridName ?? '--',
    typeCurveFormation: analysis?.wellLevel?.typeCurveFormation ?? '--',
    depthBelowLandingZoneTopFt:
      analysis?.wellLevel?.depthBelowLandingZoneTopFt ?? null,
    depthToNextZoneFt: analysis?.wellLevel?.depthToNextZoneFt ?? null,
    // A VERTICAL subject has no lateral, so the BE sends these as null. Gate on
    // the VALUE, not just on `analysis`: gating on the result alone rendered
    // "-- DEG" (verified — format2 falls back to '--', so the unit tagged along)
    // and "-- DEG (null)" for the inclination, both of which read as a real
    // measurement that happens to be missing rather than one that cannot exist.
    averageWellboreAzimuth:
      analysis?.geometryContext?.wellAzimuthDegree !== null &&
      analysis?.geometryContext?.wellAzimuthDegree !== undefined
        ? `${format2(analysis.geometryContext.wellAzimuthDegree)} DEG`
        : '--',
    averageWellboreInclination:
      analysis?.geometryContext?.wellInclinationDegree !== null &&
      analysis?.geometryContext?.wellInclinationDegree !== undefined
        ? `${format2(analysis.geometryContext.wellInclinationDegree)} DEG${
            analysis.geometryContext.toeDirection
              ? ` (${analysis.geometryContext.toeDirection})`
              : ''
          }`
        : '--',
    lateralLengthFt: analysis?.geometryContext?.lateralLengthFt ?? null,
    tortuosity: analysis?.geometryContext?.tortuosity ?? null,
    landingParameters: analysis
      ? [
          `Perforations: ${
            analysis.landingParameters.usePerforations ? 'Yes' : 'No'
          }`,
          `Inclination: ${analysis.landingParameters.horizontalInclinationCutoffDeg}°`,
          `Step: ${analysis.landingParameters.stepLengthFt} ft`,
          `Thickness: ${analysis.landingParameters.thicknessCutoffFt} ft`,
        ].join(' | ')
      : '--',
    flagsErrors: analysis?.outputSupportData ?? null,
    inZoneIntervals:
      analysis?.allInZones?.map((zone) => ({
        geologicInterval: zone.geologicInterval,
        feetInZone: zone.feetInZone,
        percentInZone: zone.percentInZone,
      })) ?? [],
  };
}

// Formats a number as feet (e.g., 8000 -> "8,000 ft")
export const formatAsFeet = (
  value: number | null | undefined
): string | null => {
  if (value === null || value === undefined) return null;
  return `${format.number(value)} ft`;
};

interface FormatListOptions {
  conjunction?: 'and' | 'or';
  separator?: string;
  verbs?: [string, string];
}

export function formatList(
  arr: string[],
  options: FormatListOptions = { conjunction: 'and', separator: ',' }
): string {
  if (arr.length === 0) return '';
  if (arr.length === 1) return `${arr[0]} ${options.verbs?.[0] ?? ''}`;

  return `${arr.slice(0, -1).join(options.separator)} ${options.conjunction} ${
    arr[arr.length - 1]
  } ${options.verbs?.[1] ?? 'are'}`;
}

export const darkenColor = (
  hex: string | undefined,
  factor: number
): string => {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return '#333333';
  const r = Math.max(0, Math.round(parseInt(hex.slice(1, 3), 16) * factor));
  const g = Math.max(0, Math.round(parseInt(hex.slice(3, 5), 16) * factor));
  const b = Math.max(0, Math.round(parseInt(hex.slice(5, 7), 16) * factor));
  return `#${r.toString(16).padStart(2, '0')}${g
    .toString(16)
    .padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// Pre-drill (permit) line on the 2D Profile: near-opaque so the pink dashed
// line stays readable over the formation bands (INS-12803).
const PRE_DRILL_LINE_ALPHA = 0.85;

export type WellLineType =
  'source' | 'sidetrack' | 'uncorrected' | 'pre_drill' | 'estimated';

export const getWellLineStyle = (
  lineType: WellLineType,
  /** Well's own color — only used by 'estimated' (pre_drill and the other
   *  types keep their fixed domain colors). */
  baseColor?: string
): {
  color: string;
  dashStyle: Highcharts.DashStyleValue;
  lineWidth: number;
} => {
  switch (lineType) {
    case 'source':
      return { color: '#000000', dashStyle: 'Solid', lineWidth: 2.5 };
    case 'sidetrack':
      return { color: '#666666', dashStyle: 'Dash', lineWidth: 1 };
    case 'uncorrected':
      return { color: '#999999', dashStyle: 'Dot', lineWidth: 1 };
    // Pre-drill permit geometry: pink dashed line, near source weight so it
    // reads as a full trajectory but stays visibly distinct.
    case 'pre_drill':
      return {
        color: withAlpha(PERMIT_MARKER_COLOR, PRE_DRILL_LINE_ALPHA),
        dashStyle: 'Dash',
        lineWidth: 2,
      };
    // Estimated (synthesized vertical) geometry: dashed + semi-transparent at
    // the well's own color, matching the 3D viewer's estimated-bore treatment
    // (opacity 0.55, thinner than a real trajectory) so a lower-confidence
    // estimate reads consistently across the 3D viewer, gun barrel, and 2D
    // Profile.
    case 'estimated':
      return {
        color: withAlpha(baseColor ?? '#888888', ESTIMATED_ALPHA),
        dashStyle: 'Dash',
        lineWidth: 1.5,
      };
  }
};

export { headerWithTooltip } from 'utils/header-with-tooltip';

// ─── Well Spacing shared ──────────────────────────────────────────────────────

export interface WellItem {
  id: string;
  label: string;
  uwi14: string | null;
}

export const ALL_WELLS_ITEM: WellItem = {
  id: '__all__',
  label: 'All Wells',
  uwi14: null,
};

export type SpacingRollupMethod =
  'minimum' | 'p20' | 'median' | 'p80' | 'maximum';

export const ROLLUP_OPTIONS: { value: SpacingRollupMethod; label: string }[] = [
  { value: 'minimum', label: 'Minimum' },
  { value: 'p20', label: 'P20' },
  { value: 'median', label: 'Median' },
  { value: 'p80', label: 'P80' },
  { value: 'maximum', label: 'Maximum' },
];

export const METHOD_LABEL: Record<SpacingRollupMethod, string> = {
  minimum: 'Minimum',
  p20: 'P20',
  median: 'Median',
  p80: 'P80',
  maximum: 'Maximum',
};

export function formatMd(ft: number, includeFt = false): string {
  return includeFt
    ? `${format0(Math.round(ft))} ft MD`
    : `${format0(Math.round(ft))} MD`;
}

// Compute compass bearing (degrees, 0=N, clockwise) from two lat/lon points.
// Used as an azimuth fallback when landing analysis hasn't been run.
export function computeBearing(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLon = toRad(lon2 - lon1);
  const y = Math.sin(dLon) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

export const buildSpacingParameters = (
  settings: LandingAnalysisSettingsValues,
  radiusStr: string,
  gunBarrelAzimuthDeg?: number | null
) => ({
  searchRadiusFt: parseFloat(radiusStr.replace(/,/g, '')),
  horizontalInclinationCutoffDeg: parseInt(settings.inclinationCutoff, 10),
  stepLengthFt: parseInt(settings.stepFrequency, 10),
  usePerforations: settings.usePerforations,
  // Omitted entirely when no azimuth is chosen — the key must be ABSENT, not 0.
  // The BE reads nil as "no section, nothing to compute" (and drops estimated
  // verticals); 0 is a valid azimuth (due North). Sending 0 for "unset" would
  // space every vertical along a direction the user never picked.
  ...(typeof gunBarrelAzimuthDeg === 'number' ? { gunBarrelAzimuthDeg } : {}),
});

// The subject spaced against its own other segments shows under its own name.
export const selfPairName = (name: string) => `${name} (own lateral)`;

/**
 * Puts the other dots of the hovered dot's well in the same state. A U-turn
 * bore draws its own other lateral as a second dot with its uwi14, so hovering
 * either one lights both, the way selecting one already does.
 * @param {Highcharts.Point} point - The hovered dot.
 * @param {'' | Highcharts.PointStateValue} state - 'hover' on enter, '' on leave.
 */
export function setTwinDotsState(
  point: Highcharts.Point,
  state: '' | Highcharts.PointStateValue
): void {
  const uwi = (point.options as { uwi14?: string }).uwi14;
  if (!uwi) return;
  for (const twin of point.series.points) {
    if (twin !== point && (twin.options as { uwi14?: string }).uwi14 === uwi) {
      twin.setState(state);
    }
  }
}

/**
 * The scenario a page starts on when it has none of its own: "pdp default",
 * else the first scenario.
 *
 * @param {DcaScenariosResponse[]} scenarios - The loaded scenario list.
 * @return {DcaScenariosResponse | null} The scenario to select, or null for an empty list.
 */
export const defaultDcaScenario = (
  scenarios: DcaScenariosResponse[]
): DcaScenariosResponse | null =>
  scenarios.find((scenario) => scenario.name === 'pdp default') ??
  scenarios[0] ??
  null;
